import { ConnectedPosition, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { DOCUMENT } from '@angular/common';
import {
  ApplicationRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { Udf } from '@core/Udf';
import { DropdownService } from '@shared/dropdown/dropdown.service';
import { SelectedOptionDirective, SelectOptionDirective } from '@ui-kit/app-select/select-option.directive';
import { inSensitiveSearch } from '@ui-kit/utility/insensitiveSearch';
import { CollectionModel } from 'collection';
import Requals from 'ramda/es/equals';

@Component({
  selector: 'app-select',
  templateUrl: './app-select.component.html',
  styleUrls: ['./app-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppSelectComponent),
      multi: true
    }
  ]
})
export class AppSelectComponent implements OnInit, OnDestroy, OnDestroy, OnChanges, ControlValueAccessor {
  @ViewChild('searchInput') searchElement: ElementRef;
  @ViewChild('scrollContainer') scrollContainer: ElementRef;

  @Input() id = null;
  @Input() dropdownClass = '';
  @Input() optionList: any[] = [];
  @Input() config: OverlayConfig = {
    maxHeight: '128px'
  };
  @Input() withoutSearch = false;
  @Input() withArrow = true;
  @Input() field: any;
  @Input() fieldKey: string;
  @Input() valueKey: any;
  @Input() displayKey = 'name';
  @Input() model: any;
  @Input() collection: CollectionModel;
  @Input() labelKey = 'name';
  @Input() placeholder = '';
  @Input() withNone = true;
  @Input() disabled: boolean;
  @Input() isFilter = false;
  @Input() filterLabel: string;
  @Input() hasBackdrop = true;
  @Input() reset: any;
  @Output() selectedValue = new EventEmitter<any>();
  @Output() refreshCallback = new EventEmitter<any>();
  @Output() dropdownOpened = new EventEmitter<any>();
  @Output() dropdownClosed = new EventEmitter<any>();

  @ContentChild(SelectOptionDirective, { static: false, read: TemplateRef })
  optionTemplate;

  @ContentChild(SelectedOptionDirective, { static: false, read: TemplateRef })
  selectedOptionTemplate;

  noResult = marker('No result found');
  noneWord = marker('None');
  allWord = marker('All');

  searchControl = new UntypedFormControl();
  private overlayRef: Udf<OverlayRef>;
  private originalOptionsList = [];
  activeItemIndex: number | null = null;

  selectedElement: any = null;
  useFocus = false;
  private _destroyed$ = new Subject();

  private keydownArrowDownUnListener: () => void;
  private keydownArrowUpUnListener: () => void;
  @Input() dropdownPosition: ConnectedPosition[] = [
    {
      originX: 'start', // left corner of the button
      originY: 'bottom', // bottom corner of the button
      overlayX: 'start', // left corner of the overlay to the origin
      overlayY: 'top', // top corner of the overlay to the origin
      offsetY: 5
    },
    {
      originX: 'start', // left corner of the button
      originY: 'top', // top corner of the button
      overlayX: 'start', // left corner of the overlay to the origin
      overlayY: 'bottom', // top corner of the overlay to the origin
      offsetY: -35
    }
  ];
  maxWidth: number;
  private el: HTMLElement;
  private previousValue;
  constructor(
    private _renderer2: Renderer2,
    private _elementRef: ElementRef,
    private _dropdownService: DropdownService,
    private _viewContainerRef: ViewContainerRef,
    private _cdr: ChangeDetectorRef,
    private _appRef: ApplicationRef,
    @Inject(DOCUMENT) private _document: HTMLDocument
  ) {
    this.el = this._elementRef.nativeElement;
  }

  onFocus(event) {
    this.useFocus = true;
    if (event) {
      this.selectedElement = event.target;
    }
  }

  @HostListener('document:keydown.Tab', ['$event'])
  onKeydownTab(event: KeyboardEvent) {
    if (this.isOpen) {
      this.closeDropdown();
    }

    if (this.useFocus) {
      this.useFocus = false;
    }
  }

  private onKeydownArrowUpHandler() {
    if (this.useFocus && this.isOpen) {
      if (this.activeItemIndex > 0) {
        this.activeItemIndex--;

        try {
          const elements = this._document.querySelectorAll('.select-item');
          const elemToScroll = elements[this.activeItemIndex];
          elemToScroll.scrollIntoView({ block: 'start', behavior: 'smooth' });
        } catch (err) {}
      } else if (this.activeItemIndex === 0 && this.withNone) {
        this.activeItemIndex = -1;
      }
    }
  }

  private onKeydownArrowDownHandler() {
    if (this.useFocus && this.isOpen) {
      if (this.optionList.length !== this.activeItemIndex) {
        if (this.activeItemIndex === null || this.activeItemIndex === -1) {
          this.activeItemIndex = 0;
          return;
        }

        this.activeItemIndex++;

        try {
          const elements = this._document.querySelectorAll('.select-item');
          const elemToScroll = elements[this.activeItemIndex + 1];
          elemToScroll.scrollIntoView({ block: 'end', behavior: 'smooth' });
        } catch (err) {}
      }
    }
  }

  get isOpen() {
    return this.overlayRef && this.overlayRef.hasAttached();
  }

  get label() {
    return String(this.model ? this.model[this.displayKey] : this.placeholder || this.noneWord);
  }

  @HostListener('window:keyup', ['$event']) keyEvent(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.closeDropdown();
    }
  }

  getOptions(op, key): string {
    return String(op[key]) || '';
  }

  private setMaxWidth(): void {
    if (this.config && this.config.width) {
      // @ts-ignore
      this.maxWidth = this.config.width;
    } else {
      this.maxWidth = this.el.offsetWidth;
    }
  }

  openDropdownOnEnter(event, template: TemplateRef<any>) {
    if (this.useFocus && !this.isOpen) {
      if (!this.disabled) {
        this.openDropdown(event, template);
      } else {
        this.useFocus = false;
      }
    } else if (this.useFocus && this.isOpen) {
      this.selectOnEnter();
    }
  }

  private selectOnEnter(): void | boolean {
    if (!this.optionList.length) {
      return false;
    }

    const selectedOption = this.optionList[this.activeItemIndex];
    if (selectedOption) {
      this.select(selectedOption, null);
      this.activeItemIndex = null;
    } else if ((!selectedOption && !this.model && this.withNone) || this.activeItemIndex === -1) {
      this.select(null, null);
      this.activeItemIndex = -1;
    } else {
      setTimeout(() => {
        this.selectedElement.focus();
      });

      this.closeDropdown();
    }
  }

  openDropdown(event, template: TemplateRef<any>): void {
    event.preventDefault();
    event.stopPropagation();

    this.previousValue = this.model;

    if (event.detail || this.useFocus) {
      // To avoid behavoiur when select opens on enter tap, on click by mouse detail === 1
      if (this.isOpen) {
        this.closeDropdown();
        return;
      }

      this.setMaxWidth();

      if (this.collection && this.refreshCallback) {
        // this.collection.clear();
        this.refreshCallback.next({ search: '', page: 1 });
      }

      this.overlayRef = this._dropdownService.open({
        template: template,
        containerRef: this._viewContainerRef,
        position: this.dropdownPosition,
        hasBackdrop: this.hasBackdrop
      });
      this.dropdownOpened.emit();

      if (this.hasBackdrop) {
        this.overlayRef.backdropClick().subscribe(() => this.closeDropdown());
      }

      this.keydownArrowDownUnListener = this._renderer2.listen('document', 'keydown.arrowdown', (event) => {
        this.onKeydownArrowDownHandler();
      });

      this.keydownArrowUpUnListener = this._renderer2.listen('document', 'keydown.arrowup', (event) => {
        this.onKeydownArrowUpHandler();
      });

      this._cdr.markForCheck();
      setTimeout(() => {
        this._appRef.tick();
      }, 16);
    }
  }

  select(option: any | null, e): void {
    const nullOption = {};
    nullOption[this.displayKey] = marker('None');
    if (option) {
      this.model = option;
    } else {
      nullOption[this.valueKey] = null;
      this.model = nullOption;
    }

    if (this.field && this.fieldKey) {
      if (this.field instanceof UntypedFormControl) {
        this.field.setValue(this.model[this.valueKey]);
      } else {
        this.field[this.fieldKey] = this.model[this.valueKey];
      }
    }

    if (this.model) {
      this.selectedValue.next({ model: this.model, field: this.field, event: e });
      this.onChangeCallback(this.model[this.valueKey]);
    }

    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    setTimeout(() => {
      this.selectedElement.focus();
    });

    this.closeDropdown();
    this._cdr.markForCheck();
  }

  resetPreviousValue(): void {
    this.model = this.previousValue;
    this.onTouchedCallback();
  }

  closeDropdown() {
    if (this.isOpen) {
      this.setDefaultIndex();
      this.useFocus = false;
      this.onTouchedCallback();
      this.overlayRef.dispose();
      this.searchControl.patchValue('');
      this.dropdownClosed.emit();

      // clear listeners
      this.keydownArrowDownUnListener();
      this.keydownArrowUpUnListener();
    }
  }

  getSelected(currentModel: any = {}): boolean {
    if (currentModel && currentModel.hasOwnProperty('id')) {
      return (this.model || {}).id === currentModel.id;
    } else {
      return Requals(this.model, currentModel);
    }
  }

  getSubItem(currentModel: any = {}): boolean {
    if (currentModel) {
      return !!currentModel.parent_id;
    }
  }

  onScroll(): void {
    if (!this.collection?.loadByScrolling) {
      return;
    }
    if (this.collection?.meta.currentPage === this.collection?.meta.pageCount) {
      return;
    }
    if (!this.collection?.loading && this.collection?.loaded) {
      const search = this.searchControl.value ? this.searchControl.value : '';
      const page = this.collection.meta.currentPage + 1;
      this.refreshCallback.next({ search, page });
    }
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  writeValue(value: any): void {}

  private search(query: string) {
    if (query.length) {
      this.optionList = this.originalOptionsList.filter((option) => inSensitiveSearch(option[this.labelKey], query));
    } else {
      this.optionList = this.originalOptionsList;
    }

    this._cdr.markForCheck();
  }

  private setDefaultIndex(): void {
    if (this.withNone) {
      this.activeItemIndex = -1;
    } else {
      this.activeItemIndex = 0;
    }
  }

  ngOnInit(): void {
    this.setDefaultIndex();

    this.searchControl.valueChanges
      .pipe(distinctUntilChanged(), debounceTime(100), takeUntil(this._destroyed$))
      .subscribe((query) => {
        if (!this.collection) {
          this.search(query);
        } else {
          if (this.isOpen) {
            this.refreshCallback.next({ search: query ? query : '', page: 1 });
          }
        }

        if (query) {
          this.setDefaultIndex();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.originalOptionsList.length === 0 && changes['optionList']) {
      this.originalOptionsList = [...this.optionList];
    }

    if (changes.hasOwnProperty('reset') && !changes.reset.firstChange) {
      this.resetPreviousValue();
    }
  }

  ngOnDestroy(): void {
    this.closeDropdown();
    this._destroyed$.next(true);
    this._destroyed$.complete();
  }
  // form view adapter implementation
  onChangeCallback = (_: any) => undefined;

  onTouchedCallback = () => undefined;
}
